import { useState } from "react";
import { toast } from "sonner";
import { ArrowRight, Check } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Reveal } from "@/components/motion";
import { claimEbook } from "@/lib/api";

/* Standalone "Get the free book" claim section — one per book page. */
export const FreeBookClaim = ({ packageId, title, cover, tagline }) => {
  const [form, setForm] = useState({ name: "", email: "" });
  const [submitting, setSubmitting] = useState(false);
  const [claimed, setClaimed] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Please enter your name and email.");
      return;
    }
    setSubmitting(true);
    try {
      const { session_id } = await claimEbook({
        customer_name: form.name.trim(),
        customer_email: form.email.trim(),
        package_id: packageId,
      });
      setClaimed({ name: form.name.trim(), email: form.email.trim(), session_id });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="bg-[#E60000] border-b-2 border-black" data-testid={`claim-section-${packageId}`}>
      <div className="max-w-[1400px] mx-auto grid grid-cols-1 lg:grid-cols-12">
        <div className="lg:col-span-5 bg-black relative flex items-center justify-center py-12 lg:py-16 border-b-2 lg:border-b-0 lg:border-r-2 border-black overflow-hidden">
          <div className="absolute inset-0 spotlight" aria-hidden="true" />
          <div className="relative w-2/3 sm:w-1/2 lg:w-3/5 border-2 border-white shadow-[12px_12px_0px_0px_#E60000]">
            <img
              src={cover}
              alt={`${title} — book cover`}
              className="w-full h-auto block"
              data-testid={`claim-cover-${packageId}`}
            />
          </div>
        </div>
        <div className="lg:col-span-7 px-6 lg:px-12 py-16 lg:py-24 text-white">
          <Reveal>
            <div className="text-xs uppercase tracking-[0.3em] font-bold text-black mb-4">Free download</div>
            <h2 className="font-display text-5xl sm:text-6xl uppercase leading-[0.9]">
              Get the book.<br />It's free.
            </h2>
            <p className="mt-4 text-lg font-medium text-white/90 max-w-xl">{tagline}</p>
          </Reveal>
          {claimed ? (
            <Reveal delay={0.05}>
              <div
                className="mt-10 border-2 border-black bg-white text-black p-6 lg:p-8 max-w-xl"
                data-testid={`claim-success-${packageId}`}
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="bg-[#E60000] text-white border-2 border-black w-10 h-10 flex items-center justify-center flex-shrink-0">
                    <Check className="w-5 h-5" strokeWidth={3} />
                  </div>
                  <div className="font-display text-3xl uppercase leading-none">Welcome. It's on its way.</div>
                </div>
                <p className="font-medium text-neutral-800 leading-relaxed">
                  What you just did is bigger than a download{claimed.name ? `, ${claimed.name}` : ""}.
                  You reached for help — and that single move is the first crack in a
                  disease that survives on silence and shame. Welcome to your recovery.
                </p>
                <p className="mt-4 font-medium text-neutral-800 leading-relaxed">
                  <strong>{title}</strong> is on its way to <strong>{claimed.email}</strong>.
                  Open the email and tap the <strong>Download</strong> button at the bottom —
                  the book is yours.
                </p>
                <p className="mt-4 text-xs uppercase tracking-widest font-bold text-neutral-600">
                  Nothing in 5 minutes? Check your spam folder.
                </p>
              </div>
            </Reveal>
          ) : (
            <Reveal delay={0.1}>
              <form
                onSubmit={submit}
                className="mt-10 border-2 border-black bg-white text-black p-6 lg:p-8 max-w-xl"
                data-testid={`claim-form-${packageId}`}
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor={`claim-name-${packageId}`} className="text-xs uppercase tracking-[0.25em] font-bold mb-2 inline-block">Your name</Label>
                    <Input
                      id={`claim-name-${packageId}`}
                      type="text"
                      placeholder="Alex Carter"
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      data-testid={`claim-name-${packageId}`}
                      className="rounded-none border-2 border-black h-14 text-lg focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:border-[#E60000]"
                    />
                  </div>
                  <div>
                    <Label htmlFor={`claim-email-${packageId}`} className="text-xs uppercase tracking-[0.25em] font-bold mb-2 inline-block">Email — we deliver here</Label>
                    <Input
                      id={`claim-email-${packageId}`}
                      type="email"
                      placeholder="alex@example.com"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      data-testid={`claim-email-${packageId}`}
                      className="rounded-none border-2 border-black h-14 text-lg focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:border-[#E60000]"
                    />
                  </div>
                </div>
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid={`claim-submit-${packageId}`}
                  className="mt-4 w-full h-14 bg-black text-white border-2 border-black font-bold uppercase tracking-widest text-sm hover:bg-[#E60000] transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
                >
                  {submitting ? "Preparing your book..." : <>Send Me The Book — Free <ArrowRight className="w-4 h-4" /></>}
                </button>
                <p className="mt-3 text-xs uppercase tracking-widest font-bold text-neutral-600">
                  Free download — no card, no catch. We'll email you the link so you have it forever.
                </p>
              </form>
            </Reveal>
          )}
        </div>
      </div>
    </section>
  );
};
